import {InteractionHandler, PointerHandler, useAnimation} from '@glark-newco/game-library';
import {FederatedPointerEvent} from '@pixi/events';
import {Graphics as PixiGraphics} from '@pixi/graphics';
import {Container, Graphics, Sprite, Text} from '@pixi/react';
import {Sound} from '@pixi/sound';
import {Assets} from 'pixi.js';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {TileSprite} from './TileSprite';
import {DRAG, TILE_BACKGROUND} from '../assetManifest';
import {textStyle} from '../constants';
import {useGameOver} from '../hooks/useGameOver';
import {useLocalPlayerTiles} from '../hooks/useLocalPlayerTiles';
import {Tile} from '../state/signlinesBoardSlice';
import {useIsGameStarted} from '../state/signlinesTimestampsSlice';
import {useTileInteraction} from '../state/useTileInteraction';
import {TILE_WIDTH} from '../utils/GameBoardUtils';


const TILE_PADDING = 5;
export const TILE_SPACING = TILE_WIDTH + TILE_PADDING;
const CAROUSEL_WIDTH = (TILE_SPACING * 4);
const animationTimeMs = 500;


interface PlayerTileProps {
    tile: Tile;
    index: number;
}

export function PlayerTile({tile, index}: PlayerTileProps) {
    const {startDragging, isDragging, handleDragEnd, setZoom, cancelDrag} = useTileInteraction();
    const {isGameOver} = useGameOver();

    const handleDragStart: PointerHandler = useCallback(() => {
        if (isGameOver) return;
        startDragging(tile, {x: 75, y: 50});
    }, [startDragging, tile, isGameOver]);

    const handleDoubleClick: PointerHandler = useCallback(() => {
        void setZoom(tile);
    }, [tile]);

    const interactionHandler = useMemo(() =>
        new InteractionHandler({handleDragStart, handleDragEnd: (event) => void handleDragEnd(event), handleDoubleClick}), []);

    useEffect(() => {
        interactionHandler.reInitialise({handleDragStart, handleDragEnd: (event) => void handleDragEnd(event), handleDoubleClick}, isDragging);
    }, [handleDragStart, isDragging, handleDragEnd, handleDoubleClick]);

    useEffect(() => {
        if (isGameOver) {
            cancelDrag();
        }
    }, [isGameOver, cancelDrag]);

    return (
        <TileSprite
            tileId={tile.tileId}
            x={index * (TILE_SPACING)} y={5}
            data-testid={`PlayerTile_${tile.tileId}`}
            anchor={0}
            eventMode={'static'}
            pointerdown={(event: FederatedPointerEvent) => !isGameOver ? interactionHandler.handleMouseDown(event) : null}
            pointerup={(event: FederatedPointerEvent) => interactionHandler.handleMouseUp(event)}
            pointerupoutside={(event: FederatedPointerEvent) => interactionHandler.handleMouseUp(event)}
        />
    );
}


function Carousel({x}: {x: number}) {
    const container = useRef<PixiGraphics | null>(null);
    const mask = useRef<PixiGraphics | null>(null);
    const lastX = useRef<number>(x);
    const [renderComplete, setRenderComplete] = useState<boolean>(false);
    const localTiles = useLocalPlayerTiles();
    useAnimation({
        animation: (progress) => {
            if (container.current && lastX.current) {
                const delta = x - lastX.current;
                container.current.x = lastX.current + (delta * progress);
            }
        },
        cleanup: () => {
            lastX.current = x;
        },
        animationTimeMs,
    }, [x]);

    const playerTiles = useMemo(() => {
        if (!renderComplete)
            // Allow the MASK to be applied to the container before we fill the container
            return <></>;
        return localTiles.map((tile, idx) =>
            <PlayerTile key={idx} tile={tile} index={idx}/>,
        );
    }, [localTiles, mask.current, renderComplete]);

    useEffect(() => {
        // Ensure this panel is rendered twice when first displayed, so that the Mask gets applied correctly
        if (!renderComplete)
            setRenderComplete(true);
    }, []);

    return (
        <>
            <Container
                y={845} ref={container}
                mask={mask.current}
                height={95} width={TILE_SPACING * localTiles.length}
            >
                {playerTiles}
            </Container>
            <Graphics
                draw={g => {
                    g.beginFill(0x000000, 0.0001);
                    g.drawRect(585, 845, CAROUSEL_WIDTH, 100);
                    g.endFill();
                }}
                ref={mask}
            />
        </>
    );
}

export function PlayersTiles() {
    const localTiles = useLocalPlayerTiles();
    const isGameStarted = useIsGameStarted();
    const [x, setX] = useState(0);

    const maxX = useMemo(() => {
        return localTiles.length <= 4 ? 0 : (TILE_SPACING * localTiles.length) - CAROUSEL_WIDTH;
    }, [localTiles]);

    useEffect(() => {
        if (x > maxX) // When a tile is removed
            setX(maxX);
    }, [maxX]);

    const handleMoveCarousel = (nx: number) => {
        setX(nx);
        const dragSound = Assets.get<Sound>(DRAG);
        dragSound.volume = 0.2;
        void dragSound.play();
    };

    if (!isGameStarted || localTiles.length === 0)
        return <></>;
    return (
        <>
            {x !== 0 &&
                <Sprite
                    image={TILE_BACKGROUND}
                    x={525} y={875}
                    width={50} height={50}
                    click={() => handleMoveCarousel((x - TILE_SPACING) < 0 ? 0 : x - TILE_SPACING)}
                    eventMode={'static'}
                >
                    <Text text={'<'} style={textStyle({fill: ['#ee4333', '#66527e'], stroke: '#ee0000'})} x={50} y={-10} scale={4}/>
                </Sprite>
            }
            <Carousel x={585 - x} />
            {x !== maxX &&
                <Sprite
                    image={TILE_BACKGROUND}
                    x={1335} y={875}
                    width={50} height={50}
                    click={() => handleMoveCarousel((x + TILE_SPACING) > (maxX) ? maxX : x + TILE_SPACING)}
                    eventMode={'static'}
                >
                    <Text text={'>'} style={textStyle({fill: ['#ee4333', '#66527e'], stroke: '#ee0000'})}  x={50} y={-10} scale={4}/>
                </Sprite>
            }
        </>
    );
}
